import { DialogActions, Grid, InputAdornment, TextField, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TwoPaneModalContent } from '../../../../../ui/components/pure/modal-content/two-pane-modal-content.component';
import { FormControl } from '../../../../../ui/components/pure/form-control/form-control.component';
import { Dropdown } from '../../../../../ui/components/pure/dropdown/dropdown.component';
import { ReceiveDeliveryActionProps, useReceiveDeliveryAction } from './receive-delivery.hook';
import { TextButton } from '../../../../../ui/components/pure/text-button/text-button.component';
import { Modal } from '../../../../../ui/components/pure/modal/modal.component';
import { ProjectLogo } from '../../../../../ui/components/pure/project-logo/project-logo.component';
import { Autocomplete } from '../../../../../ui/components/pure/autocomplete/autocomplete.component';
import { Colors } from '../../../../../ui/theme/colors';
import { ReceiveDeliveryFormConfig } from './receive-delivery-form-config';
import { Alert, AlertTitle } from '@mui/material';

export function ReceiveDeliveryModal(props: ReceiveDeliveryActionProps) {
  const { state, form, formConfig, formLayout, isLoading, onSubmitAndClose, onSubmitAndKeepOpen } =
    useReceiveDeliveryAction(props);

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  function onClickSubmitAndRestart() {
    form.handleSubmit(onSubmitAndKeepOpen)();
  }

  return (
    <Modal open title="Receive Delivery" loading={isLoading} onClose={props.onAbort}>
      <form noValidate autoComplete="off" onSubmit={form.handleSubmit(onSubmitAndClose)}>
        <TwoPaneModalContent
          leftDesktopWidth={3}
          rigthDesktopWidth={9}
          flexibleWidthPane="right"
          leftContent={() => (
            <Grid
              container
              sx={{ flexDirection: 'column' }}
              spacing={3}
              style={{ padding: '8px 12px', width: '100%', margin: 0 }}
            >
              <Grid>
                <FormControl
                  control={form.control}
                  Component={Dropdown}
                  name={formConfig.projectID.name}
                  required={formConfig.projectID.isRequired}
                  ComponentProps={{
                    fullWidth: true,
                    textFieldDesktopWidth: '100%',
                    dropdownVariant: 'floating' as const,
                    placeholder: formConfig.projectID.label,
                    required: true,
                    menuItems: formConfig.projectID.options
                  }}
                />
              </Grid>
            </Grid>
          )}
          rightContent={() => (
            <Grid
              container
              style={{ padding: '8px 12px', width: '100%', margin: 0 }}
              spacing={3}
              sx={{ flexDirection: 'column' }}
            >
              {state.error && (
                <p>
                  <Alert variant="outlined" severity="error">
                    <AlertTitle>Something went wrong</AlertTitle>
                    {state.error.message}
                  </Alert>
                </p>
              )}
              {formLayout.rows.map((row, index) => (
                <Grid key={index}>
                  <Grid container spacing={2}>
                    {formConfig[row.left.field].isVisible && (
                      <Grid
                        size={{
                          xs: 12,
                          md: row.left.desktopGridSize
                        }}
                      >
                        <InputTypeFromConfig control={form.control} config={formConfig[row.left.field]} />
                      </Grid>
                    )}
                    {formConfig[row.right.field].isVisible && (
                      <Grid
                        size={{
                          xs: 12,
                          md: row.right.desktopGridSize
                        }}
                      >
                        <InputTypeFromConfig control={form.control} config={formConfig[row.right.field]} />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              ))}
            </Grid>
          )}
        />
        <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
          <Grid
            container
            spacing={2}
            wrap="nowrap"
            sx={{
              justifyContent: 'flex-end'
            }}
          >
            <Grid
              size={{
                xs: 6,
                sm: 'auto'
              }}
            >
              <TextButton onClick={onClickSubmitAndRestart} color="secondary" variant="outlined" fullWidth={isXs}>
                Another
              </TextButton>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 'auto'
              }}
            >
              <TextButton type="submit" color="primary" variant="outlined" fullWidth={isXs}>
                Done
              </TextButton>
            </Grid>
          </Grid>
        </DialogActions>
      </form>
    </Modal>
  );
}

// Unfortunately we need to break out of the form config abstraction here because of the way the form is laid out
// The Icons in the Delivery Type dropdown push the dropdown out of alignment with the Carrier Reference input
// Here were are adding some margin to the top of the Carrier Reference input to align it with the dropdown
const useTextInputStyles = makeStyles({
  inputRoot: {
    '& .MuiInput-formControl': {
      marginTop: '21.5px'
    }
  }
});

function InputTypeFromConfig({
  control,
  config
}: {
  control: ReturnType<typeof useReceiveDeliveryAction>['form']['control'];
  config: ReceiveDeliveryFormConfig[keyof ReceiveDeliveryFormConfig];
}) {
  const classes = useTextInputStyles();
  if (config.type === 'select') {
    return (
      <FormControl
        control={control}
        name={config.name}
        required={config.isRequired}
        Component={Dropdown}
        ComponentProps={{
          fullWidth: true,
          textFieldDesktopWidth: '100%',
          dropdownVariant: 'floating' as const,
          placeholder: config.label,
          menuItems: config.options
        }}
      />
    );
  }

  if (config.type === 'autocomplete') {
    return (
      <Autocomplete
        control={control as any}
        name={config.name}
        options={config.options}
        groupBy={
          config.options.length > 0 && 'category' in config.options[0]
            ? (option: any) => (option.category as string).toLocaleUpperCase()
            : undefined
        }
        getOptionLabel={(option) => option.label}
        renderOption={(option) => (
          <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {option.label}
            {option.unit && <span style={{ marginLeft: '4px', color: Colors.midLightGray }}>{option.unit}</span>}
            {option.imageURL && <ProjectLogo imageURL={option.imageURL} alt={option.label} />}
          </div>
        )}
        fullWidth
        label={config.label}
        required={config.isRequired}
        disableClearable
        InputProps={{
          ...(config.StartAdornment && {
            startAdornment: (
              <InputAdornment position="start">
                <config.StartAdornment />
              </InputAdornment>
            )
          }),
          ...(config.EndAdornment && {
            endAdornment: (
              <InputAdornment position="end">
                <config.EndAdornment />
              </InputAdornment>
            )
          })
        }}
      />
    );
  }

  return (
    <FormControl
      control={control}
      name={config.name}
      required={config.isRequired}
      Component={TextField}
      ComponentProps={{
        classes: {
          root: classes.inputRoot
        },
        textFieldVariant: 'secondarySolid' as const,
        label: config.label,
        required: config.isRequired,
        fullWidth: true,
        slotProps: {
          input: {
            ...(config.StartAdornment && {
              startAdornment: (
                <InputAdornment position="start">
                  <config.StartAdornment />
                </InputAdornment>
              )
            }),
            ...(config.EndAdornment && {
              endAdornment: (
                <InputAdornment position="end">
                  <config.EndAdornment />
                </InputAdornment>
              )
            })
          }
        }
      }}
    />
  );
}
